import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Heart, Tag } from 'lucide-react';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

export function AdminGratitude() {
  const [suggestions, setSuggestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [impulses, setImpulses] = useState([]);
  const [newSuggestion, setNewSuggestion] = useState('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sugRes, tagRes, impRes] = await Promise.all([
        axios.get(`${API_V2}/admin/gratitude/suggestions`, { withCredentials: true }),
        axios.get(`${API_V2}/admin/gratitude/tags`, { withCredentials: true }),
        axios.get(`${API_V2}/admin/gratitude/impulses`, { withCredentials: true }),
      ]);
      setSuggestions(sugRes.data);
      setTags(tagRes.data);
      setImpulses(impRes.data);
    } catch (err) {
      console.error('Load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const addSuggestion = async () => {
    if (!newSuggestion || newSuggestion.length === 0) return;
    try {
      await axios.post(`${API_V2}/admin/gratitude/suggestions`, { text: newSuggestion }, { withCredentials: true });
      setNewSuggestion('');
      const res = await axios.get(`${API_V2}/admin/gratitude/suggestions`, { withCredentials: true });
      setSuggestions(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm('Vorschlag löschen?')) return;
    try {
      await axios.delete(`${API_V2}/admin/gratitude/suggestions/${id}`, { withCredentials: true });
      setSuggestions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  const addTag = async () => {
    if (!newTag || newTag.length === 0) return;
    try {
      await axios.post(`${API_V2}/admin/gratitude/tags`, { text: newTag }, { withCredentials: true });
      setNewTag('');
      const res = await axios.get(`${API_V2}/admin/gratitude/tags`, { withCredentials: true });
      setTags(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteTag = async (id) => {
    if (!window.confirm('Tag löschen?')) return;
    try {
      await axios.delete(`${API_V2}/admin/gratitude/tags/${id}`, { withCredentials: true });
      setTags(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteImpulse = async (id) => {
    if (!window.confirm('Diesen Dankbarkeits-Impuls unwiderruflich löschen?')) return;
    try {
      await axios.delete(`${API_V2}/admin/gratitude/impulses/${id}`, { withCredentials: true });
      setImpulses(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  if (loading) return <div className="text-gray-400">Lade...</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Dankbarkeit verwalten</h2>

      {/* Suggestions */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-3" data-testid="admin-gratitude-suggestions">
        <h3 className="font-medium text-pink-400 flex items-center gap-2"><Heart size={16} /> Dankbarkeits-Vorschläge ({suggestions.length})</h3>
        <p className="text-xs text-gray-400">Klickbare Textvorschläge für User.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSuggestion}
            onChange={e => setNewSuggestion(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addSuggestion()}
            placeholder="Neuen Vorschlag eingeben..."
            data-testid="add-suggestion-input"
            className="flex-1 rounded-lg bg-gray-700 border border-gray-600 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
          <button onClick={addSuggestion} disabled={!newSuggestion.trim()} data-testid="add-suggestion-btn" className="px-3 py-2 bg-pink-600 hover:bg-pink-700 rounded-lg text-sm text-white disabled:opacity-50 inline-flex items-center gap-1">
            <Plus size={14} /> Hinzufügen
          </button>
        </div>
        <div className="space-y-1">
          {suggestions.map(s => (
            <div key={s.id} className="flex items-center justify-between bg-gray-700/50 rounded-lg px-3 py-2">
              <span className="text-sm text-white">{s.text}</span>
              <button onClick={() => deleteSuggestion(s.id)} className="text-gray-400 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-3" data-testid="admin-gratitude-tags">
        <h3 className="font-medium text-blue-400 flex items-center gap-2"><Tag size={16} /> Tags / Kategorien ({tags.length})</h3>
        <p className="text-xs text-gray-400">Kategorien zur Einordnung der Impulse.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTag()}
            placeholder="Neuen Tag eingeben..."
            data-testid="add-tag-input"
            className="flex-1 rounded-lg bg-gray-700 border border-gray-600 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button onClick={addTag} disabled={!newTag.trim()} data-testid="add-tag-btn" className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white disabled:opacity-50 inline-flex items-center gap-1">
            <Plus size={14} /> Hinzufügen
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map(t => (
            <div key={t.id} className="inline-flex items-center gap-1.5 bg-gray-700/50 rounded-full px-3 py-1.5">
              <span className="text-sm text-white">{t.text}</span>
              <button onClick={() => deleteTag(t.id)} className="text-gray-400 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Impulses */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-3" data-testid="admin-gratitude-impulses">
        <h3 className="font-medium text-green-400">Alle Impulse ({impulses.length})</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {impulses.map(imp => (
            <div key={imp.id} className="bg-gray-700/50 rounded-lg p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white">{imp.text}</p>
                {imp.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {imp.tags.map(tag => <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-400">{tag}</span>)}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {imp.anonymous ? 'Anonym' : `${imp.userName} (${imp.userEmail})`} · {new Date(imp.createdAt).toLocaleString('de-DE')}
                </div>
              </div>
              <button onClick={() => deleteImpulse(imp.id)} className="flex-shrink-0 text-gray-400 hover:text-red-400 p-1" data-testid={`delete-impulse-${imp.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
          {impulses.length === 0 && <div className="text-gray-400 text-sm">Noch keine Impulse</div>}
        </div>
      </div>
    </div>
  );
}
